import { Link } from 'react-router-dom';
import { DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'download', label: 'Download' },
  { id: 'run-installer', label: 'Run the installer' },
  { id: 'unknown-publisher', label: 'The "unknown publisher" screen' },
  { id: 'first-launch', label: 'First launch' },
  { id: 'data-location', label: 'Where ClipRadar installs its data' },
  { id: 'updating', label: 'Updating' },
  { id: 'requirements', label: 'Requirements' },
];

export default function Body() {
  return (
    <>
      <DocSection id="download" title="Download">
        <p>
          Get the current beta build from the <Link to="/download">download page</Link> — a single Windows
          (x64) installer that already bundles the Premiere Pro panel, so there&rsquo;s nothing extra to grab
          to use ClipRadar inside Premiere.
        </p>
      </DocSection>

      <DocSection id="run-installer" title="Run the installer">
        <p>
          What you download is a small <span style={kbd}>nsis-web</span> stub — well under 1&nbsp;MB. Running
          it pulls the real install payload, about <strong>3.2&nbsp;GB</strong>, over the internet as it goes,
          so keep your connection up until it finishes.
        </p>
      </DocSection>

      <DocSection id="unknown-publisher" title={'The "unknown publisher" screen'}>
        <p>
          The beta isn&rsquo;t code-signed yet, so Windows <strong>SmartScreen</strong> flags the installer as
          coming from an &ldquo;unknown publisher.&rdquo; This is expected on every install and every update —
          it doesn&rsquo;t mean anything is wrong with your download. Click <strong>More info</strong>, then{' '}
          <strong>Run anyway</strong> to continue.
        </p>
        <DocShot slug="install" shot="smartscreen" alt="Windows SmartScreen unknown-publisher dialog" />
      </DocSection>

      <DocSection id="first-launch" title="First launch">
        <p>
          On first launch, ClipRadar downloads about <strong>13&nbsp;GB</strong> of AI models in the background
          — the visual, speech, and face models it uses to analyze footage. You don&rsquo;t need to wait for
          that to finish: you can add footage and start building your library right away.
        </p>
        <DocShot slug="install" shot="model-banner" alt="First-run model-download banner" />
      </DocSection>

      <DocSection id="data-location" title="Where ClipRadar installs its data">
        <p>
          ClipRadar keeps everything it manages under <span style={kbd}>%APPDATA%\ClipRadar</span> — downloaded
          models, your license, and the per-source analysis cache (thumbnails, embeddings, transcripts, faces).
          Your original footage is never copied there; ClipRadar only references it where it already lives on
          disk. See <Link to="/docs/reference/where-your-data-lives">Where ClipRadar stores things</Link> for
          the full map.
        </p>
      </DocSection>

      <DocSection id="updating" title="Updating">
        <p>
          ClipRadar checks for updates on launch and downloads them in the background. An &ldquo;Update
          available&rdquo; pill appears in the top bar when one is ready — click it to jump to{' '}
          <Link to="/docs/interface/settings">Settings</Link> and finish with <strong>Restart to update</strong>.
        </p>
        <DocShot slug="install" shot="auto-update" alt="Settings → Auto-update section" />
        <p>
          Updating the desktop app also updates the Premiere panel&rsquo;s interface automatically — you only
          reinstall the <span style={kbd}>.ccx</span> file when the panel itself changes shape, which release
          notes call out explicitly.
        </p>
      </DocSection>

      <DocSection id="requirements" title="Requirements">
        <p>
          Full specs — VRAM, RAM, disk space, and supported Premiere Pro versions — live on{' '}
          <Link to="/docs/reference/system-requirements">System requirements</Link>.
        </p>
      </DocSection>
    </>
  );
}
